import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { doRequest } from '@r1-frontend/do-request';
import {
    ERequestStatus,
    requestByConnection,
    RequestDto,
    ResponseDto,
} from '@r1-frontend/api-domru/api-request/v1/user/request-by-connection';

import Button, { TButtonType } from '@r1-frontend/ui-react/components/buttons/button';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';
import { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import TextWrapper from '@r1-frontend/ui-react/components/typography/TextWrapper';
import { TField } from '@r1-frontend/ui-react/organisms/Forms/NamePhoneForm';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import SpecialOffer from '~/src/components/LazyCallBack/components/SpecialOffer';
import PopupResult from '~/src/components/Popups/PopupResult';
import { POLICY_PAGE_LINK } from '~/src/constants';
import { TState } from '~/src/store';
import { selectCsrf } from '~/src/store/selectors/auth/selectCsrf';

import { sendClickContinue, sendRequestStatus, sendStartInputName } from './actions/analytics';

import * as ST from './styled';

interface IPopupResult {
    isOrderOpened: boolean,
    isResultOpened: boolean,
    resultTitle: string,
    resultText: string,
    resultType: EResultPopupStatus,
}

interface ILazyCallbackProps {
    tariffName: string,
    requestDataParams: Omit<RequestDto, 'fio' | 'phone' | 'domain' | 'ga_cid' | 'check_call_type' | 'csrf'>,

    withPopupResult?: boolean,
    withSpecialOffer?: boolean,
    onClose?: () => void,
    title?: string,
    description?: string | ReactNode,
    btnText?: string,
    btnType?: TButtonType,
    actionBtnText?: string,
    onSubmit?: (response: { ok: boolean, message: string, status: ERequestStatus }) => void,
    onCloseResultPopup?: () => void,
}

export const neededSelectors = (state: TState) => ({
    isAuth: state.auth.isAuth,
    clientPersonal: state.auth.clientPersonal,
});

const LazyCallBack = (
    {
        tariffName,
        requestDataParams,
        withPopupResult,
        withSpecialOffer,
        actionBtnText,
        title = 'Если не хотите заполнять заявку сами',
        description = <>Можно сделать это по&nbsp;телефону. Заодно поможем выбрать роутер.</>,
        btnText = 'Заказать звонок',
        btnType = 'action',
        onClose,
        onSubmit,
        onCloseResultPopup,
    }: ILazyCallbackProps,
): JSX.Element => {
    const router = useRouter();
    const { isAuth, clientPersonal } = useSelector(neededSelectors);
    const csrf = useSelector(selectCsrf);

    const [name, setName] = useState<TField>({} as TField);
    const [phone, setPhone] = useState<TField>({} as TField);
    const [startInput, setStartInput] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [popup, setPopup] = useState<IPopupResult>({
        isOrderOpened: false,
        isResultOpened: false,
        resultTitle: '',
        resultText: '',
        resultType: EResultPopupStatus.SUCCESS,
    });

    const uid = isAuth ? clientPersonal?.agreement || null : null;

    useEffect(() => {
        setIsValid(!!(phone?.isValid && name?.isValid));
    }, [name, phone]);

    const onChangeHandler = useCallback((stateInput: TField, _setState: Dispatch<SetStateAction<TField>>) => {
        if (!startInput && stateInput.value !== undefined) {
            sendStartInputName(tariffName);
            setStartInput(true);
        }
        _setState(stateInput);
    }, [startInput, tariffName]);

    const openPopupResult = useCallback((ok: boolean, message: string, status: EResultPopupStatus) => {
        const popupResult = {
            isOrderOpened: false,
            isResultOpened: true,
            resultType: status,
            resultTitle: ok ? 'Заявка на подключение отправлена' : 'Произошла ошибка',
            resultText: ok ? 'Мы свяжемся с вами в ближайшее время' : message,
        };

        setPopup(popupResult);
    }, []);

    const sendRequestHandler = useCallback(async(fio: string, phone: string) => {
        const resp = await doRequest.apiRequest<ResponseDto>(
            requestByConnection({
                ...requestDataParams,
                fio,
                phone,
                csrf,
            }),
        );

        if (resp.isSuccess) {
            const { status, message } = resp.payload;
            sendRequestStatus(tariffName, !!status, message, uid);

            return {
                ok: !!status,
                message,
                status: status ? ERequestStatus.success : ERequestStatus.error,
            };
        } else {
            sendRequestStatus(tariffName, false, null, uid);

            return {
                ok: false,
                message: 'Не удалось отправить заявку.',
                status: ERequestStatus.error,
            };
        }
    }, [requestDataParams, tariffName, uid]);

    const onSubmitHandler = useCallback(async() => {
        if (isValid) {
            const resp = await sendRequestHandler(name.value, phone.value);
            const { ok, message, status } = resp;

            openPopupResult(ok, message, status as unknown as EResultPopupStatus);

            phone?.clearInput();
            name?.clearInput();

            onSubmit && onSubmit(resp);
        }
    }, [isValid, name, phone, openPopupResult, sendRequestHandler, onSubmit]);

    const closePopupHandler = useCallback(() => {
        onCloseResultPopup && onCloseResultPopup();

        setPopup(state => ({
            ...state,
            isResultOpened: false,
        }));

        if (popup.resultType === 'success') {
            const mainPageUrl = isAuth ? '/lk' : '/';
            router.push(mainPageUrl);
        }
    }, [popup.resultType, router, isAuth]);

    const onClickContinueHandler = () => {
        sendClickContinue(tariffName);
        onClose && onClose();
    };

    return (
        <ST.LazyCallBack data-test='lazy-callback'>
            <ST.ContentWrapper>
                <TextWrapper>
                    <ST.SH3 data-test='lazy-callback-title'>{title}</ST.SH3>
                    <Paragraph4 $color={COLORS.TextInfo} data-test='lazy-callback-subtitle'>{description}</Paragraph4>
                </TextWrapper>

                <ST.Form data-test='lazy-callback-form'>
                    <ST.InputFields>
                        <ST.NameInput
                            name='name'
                            placeholder='Имя'
                            errorMessage='Ожидаются только буквы и дефисы'
                            onChange={(stateInput: TField) => onChangeHandler(stateInput, setName)}
                            className={'ym-disable-keys'}
                            data-test='lazy-callback-form-name-input'
                        />
                        <ST.PhoneInput
                            name='phone'
                            placeholder='Номер телефона'
                            errorMessage='Телефон в формате +7 (999) 999-99-99'
                            onChange={(stateInput: TField) => onChangeHandler(stateInput, setPhone)}
                            className={'ym-disable-keys'}
                            data-test='lazy-callback-form-phone-input'
                        />
                    </ST.InputFields>

                    <PolicyNote
                        data-test='lazy-callback-form-policy'
                        buttonText={btnText}
                        agreeUrl={POLICY_PAGE_LINK}
                    />

                    <Button
                        size={'large'}
                        bType={btnType}
                        disabled={!isValid}
                        onClick={onSubmitHandler}
                        data-test='lazy-callback-form-submit-button'
                    >
                        {btnText}
                    </Button>
                </ST.Form>
            </ST.ContentWrapper>

            {startInput && withSpecialOffer && (
                <SpecialOffer
                    btnText={actionBtnText}
                    onClickContinue={onClickContinueHandler}
                    data-test='lazy-callback-special-offer'
                />
            )}

            {withPopupResult && (
                <PopupResult
                    isOpen={popup.isResultOpened}
                    onClose={closePopupHandler}
                    title={popup.resultTitle}
                    text={popup.resultText}
                    type={popup.resultType}
                    data-test='lazy-callback-popup-result'
                />
            )}
        </ST.LazyCallBack>
    );
};

export default LazyCallBack;
